import { format } from "date-fns";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Calendar, TrendingUp, TrendingDown, DollarSign, Building2, Phone, Mail, MapPin, User, Clock, MessageSquare, FileText, Target } from "lucide-react";
import { cn } from "@/lib/utils";

export function InvestmentHistory({ investments, onUpdateValuation }) {
  if (!investments || investments.length === 0) {
    return (
      <p className="text-sm text-slate-400 text-center py-8">Ingen investeringer ennå</p>
    );
  }

  const totalInvested = investments.reduce((sum, inv) => sum + (inv.investment_amount || 0), 0);
  const activeInvestments = investments.filter(inv => inv.status === "active");
  const exitedInvestments = investments.filter(inv => inv.status === "exited");
  
  const totalCurrentValue = activeInvestments.reduce((sum, inv) => sum + (inv.current_valuation || inv.investment_amount || 0), 0);
  const totalExitValue = exitedInvestments.reduce((sum, inv) => sum + (inv.exit_amount || 0), 0);
  const totalRealized = totalExitValue - exitedInvestments.reduce((sum, inv) => sum + (inv.investment_amount || 0), 0);
  const totalUnrealized = totalCurrentValue - activeInvestments.reduce((sum, inv) => sum + (inv.investment_amount || 0), 0);

  return (
    <div className="space-y-4">
      {/* Summary Cards */}
      <div className="grid grid-cols-2 gap-3">
        <Card className="p-4 bg-gradient-to-br from-lime-100 to-white border-lime-100">
          <div className="flex items-center gap-2 text-zinc-900 mb-2">
            <DollarSign className="w-4 h-4" />
            <span className="text-xs font-medium">Total Investert</span>
          </div>
          <p className="text-2xl font-bold text-slate-900">{totalInvested.toLocaleString()} kr</p>
          <p className="text-xs text-slate-500 mt-1">{investments.length} investeringer</p>
        </Card>
        
        <Card className="p-4 bg-gradient-to-br from-emerald-50 to-white border-emerald-100">
          <div className="flex items-center gap-2 text-emerald-600 mb-2">
            <TrendingUp className="w-4 h-4" />
            <span className="text-xs font-medium">Total Gevinst</span>
          </div>
          <p className={cn(
            "text-2xl font-bold",
            (totalRealized + totalUnrealized) >= 0 ? "text-emerald-600" : "text-red-600"
          )}>
            {(totalRealized + totalUnrealized).toLocaleString()} kr
          </p>
          <div className="flex items-center gap-2 mt-1">
            <span className="text-xs text-emerald-600">Realisert: {totalRealized.toLocaleString()}</span>
            <span className="text-xs text-slate-400">•</span>
            <span className="text-xs text-slate-600">Urealisert: {totalUnrealized.toLocaleString()}</span>
          </div>
        </Card>
      </div>

      {/* Investment Timeline */}
      <div className="space-y-3">
        <h4 className="text-sm font-semibold text-slate-700 flex items-center gap-2">
          <Calendar className="w-4 h-4" />
          Investeringstidslinje
        </h4>
        <div className="space-y-2">
          {investments.sort((a, b) => new Date(b.investment_date || b.created_date) - new Date(a.investment_date || a.created_date)).map((inv, idx) => {
            const invested = inv.investment_amount || 0;
            const currentValue = inv.status === "exited" ? (inv.exit_amount || 0) : (inv.current_valuation || invested);
            const gain = currentValue - invested;
            const gainPercent = invested > 0 ? ((gain / invested) * 100) : 0;
            const isPositive = gain >= 0;

            return (
              <Card key={inv.id} className="p-4 hover:shadow-md transition-all">
                <div className="flex items-start justify-between mb-3">
                  <div className="flex items-start gap-3">
                    <div className="p-2 rounded-lg bg-lime-100">
                      <Building2 className="w-4 h-4 text-zinc-900" />
                    </div>
                    <div>
                      <h5 className="font-semibold text-slate-900">{inv.company_name}</h5>
                      <div className="flex items-center gap-2 mt-1">
                        <Badge variant="secondary" className="text-xs">
                          {inv.status === "active" ? "Aktiv" : inv.status === "exited" ? "Avsluttet" : "Avskrevet"}
                        </Badge>
                        {inv.ownership_percentage && (
                          <span className="text-xs text-slate-500">{inv.ownership_percentage}% eierskap</span>
                        )}
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    {inv.status === "active" && onUpdateValuation && (
                      <Button 
                        variant="ghost" 
                        size="icon" 
                        onClick={() => onUpdateValuation(inv)}
                        title="Oppdater verdivurdering"
                        className="h-8 w-8"
                      >
                        <DollarSign className="w-4 h-4 text-emerald-600 hover:text-emerald-700" />
                      </Button>
                    )}
                    <div className={cn(
                      "flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium",
                      isPositive ? "bg-emerald-50 text-emerald-700" : "bg-red-50 text-red-700"
                    )}>
                      {isPositive ? <TrendingUp className="w-3 h-3" /> : <TrendingDown className="w-3 h-3" />}
                      {gainPercent >= 0 ? "+" : ""}{gainPercent.toFixed(1)}%
                    </div>
                  </div>
                </div>

                <div className="grid grid-cols-3 gap-3 text-sm">
                  <div>
                    <p className="text-xs text-slate-400 mb-1">Investert</p>
                    <p className="font-semibold text-slate-700">{invested.toLocaleString()} kr</p>
                    {inv.investment_date && (
                      <p className="text-xs text-slate-400 mt-0.5">
                        {format(new Date(inv.investment_date), "MMM yyyy")}
                      </p>
                    )}
                  </div>
                  <div>
                    <p className="text-xs text-slate-400 mb-1">Verdi nå</p>
                    <p className="font-semibold text-slate-700">{currentValue.toLocaleString()} kr</p>
                    {inv.status === "exited" && inv.exit_date && (
                      <p className="text-xs text-slate-400 mt-0.5">
                        {format(new Date(inv.exit_date), "MMM yyyy")}
                      </p>
                    )}
                  </div>
                  <div>
                    <p className="text-xs text-slate-400 mb-1">Gevinst</p>
                    <p className={cn(
                      "font-semibold",
                      isPositive ? "text-emerald-600" : "text-red-600"
                    )}>
                      {isPositive ? "+" : ""}{gain.toLocaleString()} kr
                    </p>
                  </div>
                </div>

                {inv.notes && (
                  <div className="mt-3 pt-3 border-t border-slate-100">
                    <p className="text-xs text-slate-600">{inv.notes}</p>
                  </div>
                )}
              </Card>
            );
          })}
        </div>
      </div>
    </div>
  );
}

export function ContactInformation({ investor }) {
  const contactItems = [
    { icon: User, label: "Kontaktperson", value: investor.contact_name },
    { icon: Mail, label: "E-post", value: investor.contact_email, href: `mailto:${investor.contact_email}` },
    { icon: Phone, label: "Telefon", value: investor.contact_phone, href: `tel:${investor.contact_phone}` },
    { icon: User, label: "Ansvarlig", value: investor.responsible_person_name },
    { icon: Clock, label: "Siste kontakt", value: investor.last_contact_date ? format(new Date(investor.last_contact_date), "d. MMM yyyy") : null },
  ].filter(item => item.value);

  return (
    <div className="space-y-3">
      {contactItems.map((item, idx) => (
        <div key={idx} className="flex items-center gap-3 p-3 rounded-lg bg-slate-50 hover:bg-slate-100 transition-colors">
          <div className="p-2 rounded-lg bg-white">
            <item.icon className="w-4 h-4 text-slate-600" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-xs text-slate-400 mb-0.5">{item.label}</p>
            {item.href ? (
              <a href={item.href} className="text-sm font-medium text-zinc-900 hover:text-zinc-900 truncate block">
                {item.value}
              </a>
            ) : (
              <p className="text-sm font-medium text-slate-700 truncate">{item.value}</p>
            )}
          </div>
        </div>
      ))}
    </div>
  );
}

export function ConversationNotes({ activities }) {
  if (!activities || activities.length === 0) {
    return (
      <p className="text-sm text-slate-400 text-center py-8">Ingen samtaler registrert ennå</p>
    );
  }

  const sortedActivities = [...activities].sort((a, b) => 
    new Date(b.created_date) - new Date(a.created_date)
  );

  const activityIcons = {
    call: Phone,
    email: Mail,
    meeting: Calendar,
    note: FileText,
    deal_update: Target,
    status_change: TrendingUp,
  };

  const activityColors = {
    call: "bg-blue-50 text-blue-600",
    email: "bg-lime-100 text-zinc-900",
    meeting: "bg-emerald-50 text-emerald-600",
    note: "bg-amber-50 text-amber-600",
    deal_update: "bg-lime-100 text-zinc-900",
    status_change: "bg-rose-50 text-rose-600",
  };

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between mb-4">
        <h4 className="text-sm font-semibold text-slate-700 flex items-center gap-2">
          <MessageSquare className="w-4 h-4" />
          Samtalehistorikk
        </h4>
        <Badge variant="secondary" className="text-xs">
          {activities.length} registreringer
        </Badge>
      </div>

      <div className="space-y-2 max-h-[600px] overflow-y-auto">
        {sortedActivities.map((activity) => {
          const Icon = activityIcons[activity.type] || MessageSquare;
          const colorClass = activityColors[activity.type] || "bg-slate-50 text-slate-600";

          return (
            <Card key={activity.id} className="p-4 hover:shadow-md transition-all">
              <div className="flex items-start gap-3">
                <div className={cn("p-2 rounded-lg", colorClass)}>
                  <Icon className="w-4 h-4" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-start justify-between gap-2 mb-2">
                    <h5 className="font-semibold text-slate-900 text-sm">{activity.title}</h5>
                    <Badge variant="outline" className="text-xs whitespace-nowrap">
                      {activity.type.replace("_", " ")}
                    </Badge>
                  </div>
                  {activity.description && (
                    <p className="text-sm text-slate-600 leading-relaxed mb-3 whitespace-pre-wrap">
                      {activity.description}
                    </p>
                  )}
                  <div className="flex items-center gap-3 text-xs text-slate-400">
                    <div className="flex items-center gap-1">
                      <Clock className="w-3 h-3" />
                      {format(new Date(activity.created_date), "d. MMM yyyy 'kl.' HH:mm")}
                    </div>
                    {activity.created_by && (
                      <>
                        <span>•</span>
                        <div className="flex items-center gap-1">
                          <User className="w-3 h-3" />
                          {activity.created_by}
                        </div>
                      </>
                    )}
                  </div>
                </div>
              </div>
            </Card>
          );
        })}
      </div>
    </div>
  );
}